import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ClickableAvatar } from '../../components/UserStatsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DefaultAvatar = ({ size = 24 }) => (
  <div 
    className="rounded-full bg-slate-600 flex items-center justify-center flex-shrink-0"
    style={{ width: size, height: size }}
  >
    <Users size={size * 0.6} className="text-slate-400" />
  </div>
);

export default function RouletteMultiplayer() {
  const { user, token, refreshUser } = useAuth();
  const [gameData, setGameData] = useState({
    state: 'betting',
    countdown: 25,
    resultNumber: null,
    resultColor: null,
    bets: [],
    history: []
  });
  const [betAmount, setBetAmount] = useState('100');
  const [loading, setLoading] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const rotationRef = useRef(0);
  const lastSpinRoundRef = useRef(null);
  const lastRoundIdRef = useRef(null);
  const pollInterval = useRef(null);

  // Destructure for easier access
  const { state: gameState, countdown, resultNumber, resultColor, bets, history } = gameData;

  const redNumbers = [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36];

  // Set page title
  useEffect(() => {
    document.title = 'RBX.Casino | Roulette';
  }, []);

  // Fetch game state from backend
  const fetchGameState = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/multiplayer/roulette/state`);
      const data = response.data;
      
      const newRoundId = data.round_id;
      const isNewRound = newRoundId !== lastRoundIdRef.current;
      
      if (isNewRound) {
        lastRoundIdRef.current = newRoundId;
        if (data.state === 'betting') {
          refreshUser?.();
        }
      }

      // Single atomic state update
      setGameData({
        state: data.state,
        countdown: data.countdown || 25,
        resultNumber: (data.state === 'spinning' || data.state === 'result') ? data.result_number : null,
        resultColor: (data.state === 'spinning' || data.state === 'result') ? data.result_color : null,
        bets: data.bets || [],
        history: data.history || []
      });

    } catch (error) {
      console.error('Error fetching roulette state:', error);
    }
  }, [refreshUser]);
  
  // Effect to handle spin animation when game state changes to spinning
  useEffect(() => {
    if (gameState === 'spinning' && resultNumber !== null && lastRoundIdRef.current !== lastSpinRoundRef.current) {
      console.log('🎰 SPINNING TRIGGERED! Result:', resultNumber);
      lastSpinRoundRef.current = lastRoundIdRef.current;
      
      const segmentAngle = 360 / 37;
      const halfSegment = segmentAngle / 2;
      const numberPosition = (resultNumber / 37) * 360 + halfSegment;
      const currentRotationMod = rotationRef.current % 360;
      const rotationNeeded = (360 - numberPosition - currentRotationMod + 360) % 360;
      const newRotation = rotationRef.current + 1800 + rotationNeeded;
      
      rotationRef.current = newRotation;
      setRotation(newRotation);
      setIsSpinning(true);
    } else if (gameState === 'result' && isSpinning) {
      setIsSpinning(false);
    } else if (gameState === 'betting') {
      setIsSpinning(false);
    }
  }, [gameState, resultNumber, isSpinning]);

  // Start polling on mount
  useEffect(() => {
    fetchGameState(); // Initial fetch
    pollInterval.current = setInterval(fetchGameState, 500); // Poll every 500ms

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [fetchGameState]);

  const placeBet = async (color) => {
    if (!token) {
      toast.error('Please login to play');
      return;
    }
    if (gameState !== 'betting') {
      toast.error('Betting is closed');
      return;
    }

    const amount = parseFloat(betAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Invalid bet amount');
      return;
    }

    if (amount > (user?.site_balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/multiplayer/roulette/bet`,
        null,
        {
          params: { amount, choice: color },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`Bet R$${amount} on ${color}!`);
      refreshUser?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place bet');
    } finally {
      setLoading(false);
    }
  };

  // Filter bets by color
  const redBets = bets.filter(b => b.choice === 'red');
  const blackBets = bets.filter(b => b.choice === 'black');
  const greenBets = bets.filter(b => b.choice === 'green');

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-6">
        <h1 className="font-display text-4xl font-bold text-white mb-2">
          Rou<span className="text-red-500">lette</span>
        </h1>
      </div>

      {/* History */}
      <div className="flex justify-center gap-2 mb-6 flex-wrap">
        {history.slice(-15).map((h, i) => (
          <div
            key={h.round_id || `history-${i}`}
            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
              h.color === 'green' ? 'bg-green-500' :
              h.color === 'red' ? 'bg-red-500' : 'bg-slate-700'
            }`}
          >
            {h.number}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Wheel */}
        <div className="lg:col-span-2 glass rounded-2xl p-6 flex flex-col items-center justify-center min-h-[400px]">
          {/* Countdown / Result */}
          <div className="text-center mb-6">
            {gameState === 'betting' ? (
              <div className="text-4xl font-bold text-white">
                {countdown}s
                <p className="text-slate-400 text-lg mt-1">Place your bets!</p>
              </div>
            ) : gameState === 'spinning' ? (
              <div className="text-2xl text-yellow-400 animate-pulse">Spinning...</div>
            ) : gameState === 'result' && resultNumber !== null ? (
              <div className="text-center">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold mx-auto ${
                  resultColor === 'green' ? 'bg-green-500' :
                  resultColor === 'red' ? 'bg-red-500' : 'bg-slate-700'
                }`}>
                  {resultNumber}
                </div>
                <p className="text-xl mt-2 capitalize text-slate-300">{resultColor}</p>
              </div>
            ) : null}
          </div>

          {/* Wheel Visual - Custom rbx.casino Design */}
          <div className="relative w-80 h-80">
            {/* Outer decorative ring */}
            <div className="absolute inset-0 rounded-full border-8 border-yellow-600/60 shadow-[0_0_40px_rgba(234,179,8,0.3)]" />
            
            {/* Main spinning wheel */}
            <motion.div
              className="absolute inset-2 rounded-full overflow-hidden"
              style={{
                background: `conic-gradient(
                  ${[...Array(37)].map((_, i) => {
                    const color = i === 0 ? '#16a34a' : redNumbers.includes(i) ? '#dc2626' : '#1e293b';
                    const start = (i / 37) * 100;
                    const end = ((i + 1) / 37) * 100;
                    return `${color} ${start}% ${end}%`;
                  }).join(', ')}
                )`,
                boxShadow: 'inset 0 0 40px rgba(0,0,0,0.5)'
              }}
              animate={{ rotate: rotation }}
              transition={{ 
                duration: isSpinning ? 5 : 0.5, 
                ease: isSpinning ? [0.2, 0.0, 0.1, 1.0] : "easeOut"
              }}
            >
              {/* Numbers on the wheel */}
              {[...Array(37)].map((_, i) => {
                const angle = (i / 37) * 360 + (360 / 37 / 2); // Center in segment
                return (
                  <div
                    key={i}
                    className="absolute text-white text-[10px] font-bold flex items-center justify-center"
                    style={{
                      left: '50%',
                      top: '50%',
                      width: '20px',
                      height: '20px',
                      marginLeft: '-10px',
                      marginTop: '-10px',
                      transform: `rotate(${angle}deg) translateY(-115px)`,
                      transformOrigin: 'center center',
                      textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                    }}
                  >
                    <span style={{ transform: `rotate(${-angle}deg)` }}>
                      {i}
                    </span>
                  </div>
                );
              })}
              
              {/* Wheel spokes/dividers */}
              {[...Array(37)].map((_, i) => (
                <div
                  key={`spoke-${i}`}
                  className="absolute w-[1px] h-1/2 bg-yellow-500/50 origin-bottom"
                  style={{
                    left: '50%',
                    top: '0',
                    transform: `rotate(${(i / 37) * 360}deg)`
                  }}
                />
              ))}
            </motion.div>
            
            {/* Pointer/Marker at top */}
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-20">
              <div className="w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-t-[20px] border-t-yellow-400" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
            </div>
            
            {/* Inner ring */}
            <div className="absolute inset-[90px] rounded-full border-2 border-yellow-500/30" />
            
            {/* Center hub with rbx.casino logo */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-gradient-to-br from-slate-800 via-slate-900 to-black flex flex-col items-center justify-center border-4 border-yellow-600/50 shadow-xl z-10">
              <span className="font-display text-white font-black text-base tracking-tight">RBX</span>
              <span className="font-display text-red-500 font-black text-xs">.CASINO</span>
            </div>
          </div>
        </div>

        {/* Betting Panel */}
        <div className="glass rounded-2xl p-4 space-y-4">
          <div>
            <Label className="text-slate-300 text-sm">Bet Amount</Label>
            <Input
              type="number"
              value={betAmount}
              onChange={(e) => setBetAmount(e.target.value)}
              className="input-default font-mono"
              disabled={gameState !== 'betting'}
            />
            <div className="flex gap-2 mt-2">
              {[50, 100, 500, 1000].map(amt => (
                <button
                  key={amt}
                  onClick={() => setBetAmount(amt.toString())}
                  className="px-2 py-1 rounded bg-slate-800 text-slate-300 text-xs hover:bg-slate-700"
                  disabled={gameState !== 'betting'}
                >
                  {amt}
                </button>
              ))}
            </div>
          </div>

          {/* Bet Buttons */}
          <div className="space-y-3">
            {/* Red */}
            <div>
              <Button
                onClick={() => placeBet('red')}
                disabled={loading || gameState !== 'betting' || !user}
                className="w-full bg-red-600 hover:bg-red-500 py-6 text-lg"
              >
                {loading ? <Loader2 className="animate-spin" /> : 'Red (2x)'}
              </Button>
              {redBets.length > 0 && (
                <div className="space-y-1 mt-2 max-h-24 overflow-y-auto">
                  {redBets.map((b, i) => (
                    <div key={i} className="flex items-center gap-2 p-1.5 bg-red-500/10 rounded-lg">
                      <ClickableAvatar
                        userId={b.user_id}
                        username={b.username}
                        avatarUrl={b.avatar}
                        size={24}
                        borderColor="border-red-500/50"
                      />
                      <span className="text-xs text-white truncate flex-1">{b.username}</span>
                      <span className="text-red-400 font-mono text-xs">R${b.amount}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Green */}
            <div>
              <Button
                onClick={() => placeBet('green')}
                disabled={loading || gameState !== 'betting' || !user}
                className="w-full bg-green-600 hover:bg-green-500 py-6 text-lg"
              >
                {loading ? <Loader2 className="animate-spin" /> : 'Green (14x)'}
              </Button>
              {greenBets.length > 0 && (
                <div className="space-y-1 mt-2 max-h-24 overflow-y-auto">
                  {greenBets.map((b, i) => (
                    <div key={i} className="flex items-center gap-2 p-1.5 bg-green-500/10 rounded-lg">
                      <ClickableAvatar
                        userId={b.user_id}
                        username={b.username}
                        avatarUrl={b.avatar}
                        size={24}
                        borderColor="border-green-500/50"
                      />
                      <span className="text-xs text-white truncate flex-1">{b.username}</span>
                      <span className="text-green-400 font-mono text-xs">R${b.amount}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Black */}
            <div>
              <Button
                onClick={() => placeBet('black')}
                disabled={loading || gameState !== 'betting' || !user}
                className="w-full bg-slate-700 hover:bg-slate-600 py-6 text-lg"
              >
                {loading ? <Loader2 className="animate-spin" /> : 'Black (2x)'}
              </Button>
              {blackBets.length > 0 && (
                <div className="space-y-1 mt-2 max-h-24 overflow-y-auto">
                  {blackBets.map((b, i) => (
                    <div key={i} className="flex items-center gap-2 p-1.5 bg-slate-700/50 rounded-lg">
                      <ClickableAvatar
                        userId={b.user_id}
                        username={b.username}
                        avatarUrl={b.avatar}
                        size={24}
                        borderColor="border-slate-500/50"
                      />
                      <span className="text-xs text-white truncate flex-1">{b.username}</span>
                      <span className="text-slate-300 font-mono text-xs">R${b.amount}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {user ? (
            <div className="text-center text-slate-500 text-sm pt-2 border-t border-slate-700">
              Balance: R$ {user.site_balance?.toLocaleString()}
            </div>
          ) : (
            <div className="space-y-2 pt-2">
              <p className="text-center text-slate-400 text-sm">Login to play</p>
              <a href="/login" className="block btn-primary text-center py-3 rounded-lg">Sign In</a>
            </div>
          )}

          {/* Players count */}
          <div className="flex items-center justify-center gap-2 pt-2 border-t border-slate-700">
            <Users size={16} className="text-slate-400" />
            <span className="text-slate-400 text-sm">{bets.length} bets this round</span>
          </div>
        </div>
      </div>
    </div>
  );
}
